/**
 * ¿Lo ESCRITO en una ficha apunta un riesgo, o solo dice que no hay ninguno?
 *
 * ESTE ARCHIVO ES LA COPIA DE `backend/services/riesgo_clinico.py`. Lo que se
 * cambie en uno hay que cambiarlo en el otro, y por eso los dos se prueban con
 * LA MISMA LISTA de frases de ejemplo de cómo se escribe en una ficha
 * (tests/test_alergia_escrita_manda.py y InfoClinicaPaciente.test.jsx): si se
 * separan, salta una prueba y no un paciente.
 *
 * POR QUÉ HAY UNA COPIA (2026-09-21): el recuadro de «información
 * clínica» que sale al dar una cita y la historia clínica de la ficha leían la
 * casilla A SOLAS. Con {alergias: false, alergias_text: 'LATEX (nota quirófano
 * 2026-08-20)'} —el estado que fabrica la propia aplicación: el cuestionario
 * deja la casilla en «no» y el volcado de la nota de quirófano escribe el
 * látex al lado sin tocarla— pintaban «No». El 2026-09-18 eso se arregló en el
 * parte del día, el cuadrante y la hoja de anestesia; estos dos lectores se
 * quedaron fuera, así que el quirófano se enteraba y la consulta no.
 *
 * LO DEFINITIVO sería que el servidor mandara el texto ya resuelto en la
 * propia ficha (`GET /api/patients/{id}`), que es lo que de verdad evita que
 * vuelvan a separarse. Mientras tanto, la regla está escrita una sola vez en
 * el frontend y no dos.
 *
 * LA OTRA MITAD, que pesa lo mismo: si el aviso salta de más, la pantalla se
 * llena de «REVISAR» y deja de leerse, que es otra forma de que la alergia no
 * se vea. Por eso un texto solo cuenta como riesgo cuando dice algo que NO es
 * una negación: «no refiere», «ninguna conocida», «NRAC» o «sin alergias
 * conocidas» no encienden nada; «látex» o «penicilina», sí. Y lo que afirma
 * sin nombrar la sustancia («Alérgica», «Tiene alergias») también enciende:
 * sin sustancia solo es «no hay» cuando lleva una negación (22/9/2026).
 *
 * Y UNA NEGACIÓN SOLO NIEGA LO QUE NIEGA (23/9/2026). Con la regla del 22/9
 * bastaba un «no» en cualquier punto para que la frase entera contara como
 * «no hay»: «Alergia a fármacos, no especifica» o «Tiene alergias, no sabe a
 * qué» no encendían nada aquí, igual que en el parte y en la hoja de
 * anestesia. Ahora, igual que en el backend (su docstring lo cuenta entero):
 * el «no sabe / no recuerda / no especifica» es del saber y no niega lo de
 * al lado; una negación niega lo de detrás, y lo de delante solo cuando es
 * la respuesta a una etiqueta («Alergias: no»); y se trocea también por
 * coma, punto, paréntesis y raya, con la respuesta pegada a su etiqueta.
 */

// Lo que se imprime cuando la ficha se contradice, igual que en el parte del
// día y en la hoja de anestesia.
export const AVISO_REVISAR = 'REVISAR:';

// La coletilla que pone LA CASA al final de lo que escribió una persona, y que
// por tanto no es un dato clínico: «(nota quirófano 2026-08-20)», «(cuestionario)»,
// «(importación)» y «(comprobación automática)». Ojo: solo esas; el
// «(contraste)» de «ALERGIA AL YODO (contraste)» es del médico y se queda.
const FIRMA_DE_LA_CASA = /\((?:nota|parte|cuestionario|importaci[óo]n|comprobaci[óo]n)[^)]*\)/gi;

// Palabras que NO son un dato clínico, POR FAMILIAS (23/9/2026): la regla
// depende de qué hace cada palabra —negar, nombrar el asunto, contarlo,
// calificarlo, decir que no se sabe o rellenar—. Todo lo que no esté en una
// familia es un dato. Son las mismas que en el backend, familia a familia
// (lo comprueba tests/test_parte_alergias.py).

// Las que NIEGAN. Sin una de estas, «Alérgica» o «Tiene alergias» son un sí
// (22/9/2026). «RAMC», «RAC» y «AMC» no están: a secas son el nombre del
// asunto, y el «no» lo pone la palabra de al lado.
export const NEGACIONES = new Set([
  'no', 'ni', 'nunca', 'ninguna', 'ninguno', 'ningun', 'ningunas', 'ningunos',
  'nada', 'niega', 'niegan', 'negativo', 'negativa', 'negativos', 'negativas',
  'sin', 'nrac', 'namc', 'nramc', 'nrma',
]);

// Las que NOMBRAN EL ASUNTO: la etiqueta, no el dato. Un pedazo que nombra el
// asunto Y LO AFIRMA empieza una frase nueva: «NAMC, intolerancia
// alimentaria» son dos cosas, y la segunda es un riesgo.
export const NOMBRAN_EL_ASUNTO = new Set([
  'alergia', 'alergias', 'alergica', 'alergico', 'alergicas', 'alergicos',
  'intolerancia', 'intolerancias', 'reaccion', 'reacciones',
  'ramc', 'rac', 'amc',
  'antecedente', 'antecedentes', 'intervencion', 'intervenciones',
  'cirugia', 'cirugias', 'iq',
]);

// De las de arriba, las que ya dicen que el paciente LO ES («Alérgica»): una
// afirmación entera, que solo deshace un «no» a secas.
export const DICEN_QUE_LO_ES = new Set([
  'alergica', 'alergico', 'alergicas', 'alergicos',
]);

// Las que AFIRMAN contándolo: el verbo. Detrás de un «no» son la respuesta
// («Alergias: no refiere»).
export const AFIRMAN = new Set([
  'tiene', 'toma', 'refiere', 'refieren', 'refiera', 'referida', 'referidas',
  'consta', 'constan',
]);

// Las que CALIFICAN el asunto. Detrás de un «no» restringen, no responden:
// «Refiere alergia, no medicamentosa» sigue siendo una alergia.
export const CALIFICAN = new Set([
  'adversa', 'adversas', 'medicamento', 'medicamentos', 'medicamentosa',
  'medicamentosas', 'farmaco', 'farmacos', 'alimentaria', 'alimentarias',
  'alimenticia', 'alimenticias', 'quirurgica', 'quirurgicas', 'quirurgico',
  'quirurgicos', 'previa', 'previas', 'previo', 'previos',
]);

// Las que, detrás de un «no», un «sin» o un «ni», dicen que NO SE SABE: «no
// sabe», «sin especificar». Ese «no» es del saber y no niega lo de al lado
// («Alergia a fármacos, no especifica» es una alergia); a secas no es un «no
// hay». Sueltas, sin la negación delante, son relleno.
export const NO_SE_SABE = new Set([
  'sabe', 'saben', 'recuerda', 'recuerdan', 'contesta', 'contestan',
  'contestado', 'contestada', 'contestar', 'responde', 'responden',
  'respondido', 'respondida', 'especifica', 'especifican', 'especificado',
  'especificada', 'especificados', 'especificadas', 'especificar',
  'detalla', 'detallan', 'detallado', 'detallada', 'detallados',
  'detalladas', 'detallar',
  // Las que dicen «no se sabe» sin negación delante (23/9/2026): «Desconoce»,
  // «Lo desconoce», «NS/NC». Salían rojas mientras «No sabe» salía ámbar.
  'desconoce', 'desconocen', 'desconozco', 'desconocido', 'desconocida',
  'desconocidos', 'desconocidas', 'ns', 'nc', 'nsnc',
]);

// Lo que puede ir entre el «no» y el «sabe»: «no LO sabe», «no SE sabe», y
// delante de las que no llevan negación: «LO desconoce». Fuera de esas
// parejas no son palabras sin dato. Y «no sé» / «no lo sé»: sin tilde, «sé»
// es «se», así que un «no» seguido solo de estas hasta el final del pedazo
// es un «no se sabe» (23/9/2026).
export const ENLACES_DEL_NO_SE_SABE = new Set(['lo', 'le', 'se']);

// El RELLENO: palabras que solas no dicen ni que sí ni que no.
export const RELLENO = new Set([
  // Cómo se escribe que no hay nada.
  'conocida', 'conocidas', 'conocido', 'conocidos', 'procede', 'aparente',
  'aparentes', 'paciente', 'momento', 'ahora', 'actualmente',
  // Las muletillas de «hasta hoy no hay nada» (22/9/2026): «Sin alergias
  // conocidas hasta la fecha», «… según la paciente», «Sin alergias que
  // sepa», «… a día de hoy». El backend las tenía y esta copia no: la misma
  // ficha salía «Sin alergias» en el parte y «REVISAR» en la historia clínica.
  'hasta', 'fecha', 'hoy', 'dia', 'segun', 'sepa',
  // Palabras de enlace.
  'a', 'al', 'de', 'del', 'la', 'las', 'el', 'los', 'un', 'una', 'unos',
  'unas', 'y', 'o', 'que', 'por', 'en',
]);

export const PALABRAS_SIN_DATO = new Set([
  ...NEGACIONES, ...NOMBRAN_EL_ASUNTO, ...AFIRMAN, ...CALIFICAN,
  ...NO_SE_SABE, ...RELLENO,
]);

// Las que afirman algo si nada las niega.
const AFIRMATIVAS = new Set([...NOMBRAN_EL_ASUNTO, ...AFIRMAN, ...CALIFICAN]);

// Las negaciones que, delante de «sabe», «recuerda»…, son del saber.
const NIEGAN_EL_SABER = new Set(['no', 'sin', 'ni']);

// Dónde se corta un trozo en pedazos: coma, punto, paréntesis, corchete y la
// raya con espacios. El guion pegado no corta.
const CORTES = /[,.()[\]]| [-–—] /;

// Lo que dice una frase.
const RIESGO = 'riesgo';
const NIEGA = 'niega';
const NADA = 'nada';

// Una sigla escrita con puntos es UNA palabra: «N.R.A.C.» es «nrac» (una
// negación) y «A.A.S.» es «aas» (la aspirina, un dato). Solo con puntos
// entre las letras: «y a» son dos palabras de enlace, no una sigla.
const SIGLA_CON_PUNTOS = /\b[a-z](?:\.[a-z])+\b\.?/g;

/** Minúsculas y sin tildes: «Alérgica» y «alergica» son la misma palabra. */
function plano(texto) {
  return String(texto).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Los pedazos que hay que juzgar por separado: el texto de una ficha es la
 *  suma de lo que ha ido escribiendo cada uno («·» lo junta la nota de
 *  quirófano, «;» el cuestionario, y la fusión añade líneas). «Sin alergias
 *  conocidas» arriba y «LATEX» debajo es un riesgo. */
function trozos(texto) {
  const salida = [];
  String(texto).split('\n').forEach((linea) => {
    const l = linea.trim();
    if (!l || l.startsWith('—')) return;
    l.split(/ · |;/).forEach((t) => {
      const limpio = t.replace(FIRMA_DE_LA_CASA, '')
        .replace(/^[\s.,;:\-·/|]+|[\s.,;:\-·/|]+$/g, '');
      if (limpio) salida.push(limpio);
    });
  });
  return salida;
}

/** Las palabras del trozo que cuentan, en plano y con las siglas juntas. Las
 *  de una letra y los números sueltos no cuentan: un «0» o una «X» en el
 *  detalle no dicen ni que sí ni que no. */
function palabras(trozo) {
  const juntas = plano(trozo).replace(SIGLA_CON_PUNTOS, (s) => s.replace(/\./g, ''));
  return (juntas.match(/[a-z0-9]+/g) || []).filter((p) => p.length > 1 && !/^\d+$/.test(p));
}

/** Las palabras sin las parejas del «no se sabe» («no sabe», «no lo sabe»,
 *  «sin especificar»): ese «no» no niega el asunto (23/9/2026). Y también
 *  «no sé» / «no lo sé» (el «no» seguido solo de «lo/le/se» hasta el final
 *  del pedazo) y «lo desconoce» / «se desconoce» (el enlace delante de una
 *  palabra de la familia que no lleva negación). */
function sinElNoSeSabe(ps) {
  const fuera = [];
  let i = 0;
  const n = ps.length;
  while (i < n) {
    const p = ps[i];
    const niega = NIEGAN_EL_SABER.has(p);
    if (niega || ENLACES_DEL_NO_SE_SABE.has(p)) {
      let j = niega ? i + 1 : i;
      while (j < n && ENLACES_DEL_NO_SE_SABE.has(ps[j])) j += 1;
      if (j < n && NO_SE_SABE.has(ps[j])) {
        i = j + 1;
        continue;
      }
      if (niega && j === n && j > i + 1 && ps[j - 1] === 'se') {
        i = j;
        continue;
      }
    }
    fuera.push(p);
    i += 1;
  }
  return fuera;
}

/** Las palabras de cada pedazo del trozo, cortado por comas, puntos,
 *  paréntesis y rayas. Las siglas con puntos se juntan antes de cortar
 *  («N.R.A.C.» es una palabra), y el punto de detrás de la sigla sí corta. */
function pedazos(trozo) {
  const juntas = plano(trozo).replace(
    SIGLA_CON_PUNTOS, (s) => s.replace(/\./g, '') + (s.endsWith('.') ? '.' : ''));
  return juntas.split(CORTES).map((p) => sinElNoSeSabe(palabras(p)));
}

/** ¿La negación de `ps[i]` niega también lo de DELANTE? Solo cuando es la
 *  respuesta a una etiqueta o a una pregunta: a secas («Alergias: no»), o
 *  con un verbo o el asunto detrás si lo de delante es una etiqueta
 *  («Alergias: no refiere»). Con un calificativo detrás restringe
 *  («Refiere alergia, no medicamentosa»), y una afirmación entera («Tiene
 *  alergias», «Alérgica») solo la deshace un «no» a secas. */
function respondeALoDeDelante(ps, i) {
  const siguiente = ps.slice(i + 1).find(
    (p) => !RELLENO.has(p) && !NEGACIONES.has(p) && !NO_SE_SABE.has(p));
  if (siguiente === undefined) return true;
  if (CALIFICAN.has(siguiente)) return false;
  const delante = ps.slice(0, i);
  const afirmacionEntera = delante.some((p) => DICEN_QUE_LO_ES.has(p))
    || (delante.some((p) => AFIRMAN.has(p)) && delante.some((p) => NOMBRAN_EL_ASUNTO.has(p)));
  return !afirmacionEntera;
}

/** Lo que dice una frase: RIESGO en cuanto queda un dato, o una palabra que
 *  afirma sin ninguna negación que la alcance; NIEGA cuando todo lo que
 *  afirma lo alcanza una negación; NADA si solo hay relleno o nada. */
function juicio(ps) {
  if (!ps.length) return NADA;
  if (ps.some((p) => !PALABRAS_SIN_DATO.has(p))) return RIESGO;
  const alcanzada = ps.map(() => false);
  let hayNegacion = false;
  ps.forEach((p, i) => {
    if (!NEGACIONES.has(p)) return;
    hayNegacion = true;
    for (let j = i + 1; j < ps.length; j += 1) alcanzada[j] = true;
    if (respondeALoDeDelante(ps, i)) {
      for (let j = 0; j < i; j += 1) alcanzada[j] = true;
    }
  });
  if (ps.some((p, j) => AFIRMATIVAS.has(p) && !alcanzada[j])) return RIESGO;
  return hayNegacion ? NIEGA : NADA;
}

/** Las frases del trozo: un pedazo que nombra el asunto y, él solo, lo
 *  afirma empieza una frase nueva; lo demás (la respuesta, la muletilla, la
 *  lista que sigue a una negación, el «no se sabe») sigue a la de antes. */
function frases(trozo) {
  const salida = [];
  let frase = [];
  pedazos(trozo).forEach((ps) => {
    const empieza = frase.length > 0
      && ps.some((p) => NOMBRAN_EL_ASUNTO.has(p)) && juicio(ps) === RIESGO;
    if (empieza) {
      salida.push(frase);
      frase = [];
    }
    frase = frase.concat(ps);
  });
  if (frase.length) salida.push(frase);
  return salida;
}

/** ¿Ese texto apunta un riesgo de verdad, o solo dice que no hay ninguno? */
export function hayRiesgoEscrito(texto) {
  if (typeof texto !== 'string' || !texto.trim()) return false;
  return trozos(texto).some((t) => frases(t).some((f) => juicio(f) === RIESGO));
}

/**
 * Lo ESCRITO cuando la casilla dice «no» y el texto dice otra cosa, o ''.
 * Con la casilla en «sí», o sin marcar, no hay contradicción que avisar.
 */
export function textoQueContradice(valor, texto) {
  if (valor !== false) return '';
  const escrito = (texto || '').trim();
  return hayRiesgoEscrito(escrito) ? escrito : '';
}
